/**
 * Student Management Hooks
 * Coláiste Lurgan Management System - Student data management
 */

import { useQuery, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys, invalidateQueries, optimisticUpdates } from '../lib/queryClient';

// Get all students with filters
export function useStudents(filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.students.list(filters),
    queryFn: () => api.students.getAll(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh
    cacheTime: 10 * 60 * 1000, // 10 minutes - cache retained when unmounted
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    refetchOnMount: true, // Refetch if data is stale on mount (ensures updates are visible)
    ...options,
  });
}

// Get infinite students (for pagination)
export function useInfiniteStudents(filters = {}, options = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.students.infinite(filters),
    queryFn: ({ pageParam = 1 }) =>
      api.students.getAll({ ...filters, page: pageParam }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.next) return undefined;
      const url = new URL(lastPage.next);
      return parseInt(url.searchParams.get('page'));
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.previous) return undefined;
      const url = new URL(firstPage.previous);
      return parseInt(url.searchParams.get('page'));
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    ...options,
  });
}

// Get single student
export function useStudent(studentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.students.detail(studentId),
    queryFn: () => api.students.getById(studentId),
    enabled: !!studentId,
    ...options,
  });
}

// Get student analytics
export function useStudentAnalytics(options = {}) {
  return useQuery({
    queryKey: queryKeys.students.analytics(),
    queryFn: api.students.getAnalytics,
    staleTime: 2 * 60 * 1000, // Analytics can be cached for 2 minutes
    ...options,
  });
}

// Create student mutation
export function useCreateStudent(options = {}) {
  return useMutation({
    mutationFn: api.students.create,
    onSuccess: (newStudent) => {
      // Optimistically add to lists
      optimisticUpdates.addStudent(newStudent);

      // Invalidate students queries
      invalidateQueries.students();

      // Also invalidate related data
      invalidateQueries.courses();
      invalidateQueries.houses();
      invalidateQueries.dashboard();
    },
    ...options,
  });
}

// Update student mutation
export function useUpdateStudent(options = {}) {
  return useMutation({
    mutationFn: ({ id, ...data }) => api.students.update(id, data),
    onMutate: async ({ id, ...newData }) => {
      // Optimistically update student in cache
      optimisticUpdates.updateStudent(id, newData);

      // Return context for rollback
      return { id, previousData: newData };
    },
    onSuccess: (updatedStudent) => {
      // Invalidate specific student and lists (use UUID, not integer ID)
      invalidateQueries.student(updatedStudent.uuid);

      // Invalidate dashboard analytics (student changes affect stats)
      invalidateQueries.dashboard();
    },
    onError: (error, variables, context) => {
      // On error, invalidate to refetch real data
      if (context?.id) {
        invalidateQueries.student(context.id);
      }
    },
    ...options,
  });
}

// Delete student mutation
export function useDeleteStudent(options = {}) {
  return useMutation({
    mutationFn: api.students.delete,
    onMutate: async (studentId) => {
      // Optimistically remove from cache
      optimisticUpdates.removeStudent(studentId);

      return { studentId };
    },
    onSuccess: (_, studentId) => {
      // Invalidate queries
      invalidateQueries.students();

      // Also invalidate related data
      invalidateQueries.courses();
      invalidateQueries.houses();
      invalidateQueries.dashboard();
      invalidateQueries.friendships();
    },
    onError: (error, studentId) => {
      // On error, invalidate to refetch real data
      invalidateQueries.students();
    },
    ...options,
  });
}

// Bulk create students mutation
export function useBulkCreateStudents(options = {}) {
  return useMutation({
    mutationFn: api.students.bulkCreate,
    onSuccess: (result) => {
      // Invalidate all student-related queries
      invalidateQueries.students();
      invalidateQueries.courses();
      invalidateQueries.houses();
      invalidateQueries.dashboard();
    },
    ...options,
  });
}

// Score student interview mutation (ADMIN, OFFICE, INTERVIEWER only)
export function useScoreInterview(options = {}) {
  return useMutation({
    mutationFn: ({ studentUuid, score, notes }) =>
      api.students.scoreInterview(studentUuid, { score, notes }),
    onSuccess: (updatedStudent) => {
      // Invalidate specific student to refresh with new score data
      invalidateQueries.student(updatedStudent.uuid);
      // Also invalidate student lists to update any score displays
      invalidateQueries.students();
    },
    onError: (error, variables) => {
      // On error, invalidate to ensure UI shows real data
      if (variables?.studentUuid) {
        invalidateQueries.student(variables.studentUuid);
      }
    },
    ...options,
  });
}

// Student search hook with debouncing
export function useStudentSearch(searchTerm, options = {}) {
  return useQuery({
    queryKey: queryKeys.students.list({ search: searchTerm }),
    queryFn: () => api.students.getAll({ search: searchTerm }),
    enabled: searchTerm && searchTerm.length >= 2, // Only search with 2+ characters
    staleTime: 30 * 1000, // Search results are fresh for 30 seconds
    ...options,
  });
}

// Hook for student form data dependencies (optimized - only fetches what's actually used)
export function useStudentFormData() {
  const schoolYears = useQuery({
    queryKey: queryKeys.schoolYears.all(),
    queryFn: () => api.schoolYears.getAll(),
    staleTime: 60 * 60 * 1000, // 1 hour - school years rarely change
    onError: (error) => { /* SchoolYears API Error logged */ },
  });

  const travelLocations = useQuery({
    queryKey: queryKeys.travelLocations.all(),
    queryFn: () => api.travelLocations.getAll(),
    staleTime: 60 * 60 * 1000, // 1 hour - travel locations rarely change
    onError: (error) => { /* TravelLocations API Error logged */ },
  });

  const courses = useQuery({
    queryKey: queryKeys.courses.lists(),
    queryFn: () => api.courses.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => { /* Courses API Error logged */ },
  });

  // Note: houses and classes removed - not used in StudentRegistrationForm
  // If needed elsewhere, create separate hooks: useHouses() and useClasses()

  // Form data debugging information available:
  // schoolYearsData, schoolYearsError, schoolYearsLoading,
  // coursesData, coursesError, coursesLoading, travelLocationsError

  return {
    schoolYears: schoolYears.data?.results || schoolYears.data || [],
    travelLocations: travelLocations.data?.results || travelLocations.data || [],
    // Consumers of this list have different needs for archived courses: forms that
    // assign a course to a student (StudentRegistrationForm, StudentDetail) disable
    // them; pickers that find/target existing students (StudentsPage filter,
    // RecipientSelector) keep them selectable and just label them clearly. Filtering
    // them out here would break the latter, so the full list is returned as-is.
    courses: courses.data?.results || courses.data || [],
    isLoading:
      schoolYears.isLoading ||
      travelLocations.isLoading ||
      courses.isLoading,
    error:
      schoolYears.error ||
      travelLocations.error ||
      courses.error,
  };
}

// Separate hooks for houses and classes if needed elsewhere
export function useHouses(options = {}) {
  return useQuery({
    queryKey: queryKeys.houses.lists(),
    queryFn: () => api.houses.getAll({ is_active: true }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useClasses(options = {}) {
  return useQuery({
    queryKey: queryKeys.classes.lists(),
    queryFn: () => api.classes.getAll({ is_active: true }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

// ============================================
// Friendship Management Hooks
// ============================================

// Get all friendships for a specific student (including transitive friendships)
export function useStudentFriendships(studentUuid, options = {}) {
  return useQuery({
    queryKey: ['friendships', 'student', studentUuid],
    queryFn: async () => {
      // Use backend student_group endpoint to get ALL friendships in the group
      // This includes both direct friendships AND transitive friendships
      // Example: If A-B and B-C exist, querying for A returns both A-B and B-C
      const response = await api.friendships.getStudentGroup(studentUuid);
      return response.results || response || [];
    },
    enabled: !!studentUuid,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// Add a new friendship
export function useAddFriendship(options = {}) {
  return useMutation({
    mutationFn: api.friendships.create,
    onSuccess: (newFriendship) => {
      // Invalidate friendship queries for both students
      invalidateQueries.friendships();

      // Also invalidate the students themselves (has_friends flag may change)
      if (newFriendship.student1) {
        invalidateQueries.student(newFriendship.student1);
      }
      if (newFriendship.student2) {
        invalidateQueries.student(newFriendship.student2);
      }
    },
    ...options,
  });
}

// Remove a friendship
export function useRemoveFriendship(options = {}) {
  return useMutation({
    mutationFn: api.friendships.delete,
    onSuccess: (_, friendshipUuid) => {
      // Invalidate all friendship queries
      invalidateQueries.friendships();

      // Invalidate students list (has_friends flag may change)
      invalidateQueries.students();
    },
    ...options,
  });
}

// Resolve a single upload issue by index (ADMIN/OFFICE only)
export function useResolveIssue(options = {}) {
  return useMutation({
    mutationFn: ({ studentUuid, index }) =>
      api.students.resolveIssue(studentUuid, index),
    onSuccess: (updatedStudent) => {
      invalidateQueries.student(updatedStudent.uuid);
      invalidateQueries.students();
    },
    ...options,
  });
}

// Flag student as ACTION_REQUIRED with a custom note (ADMIN/OFFICE only)
export function useFlagStudent(options = {}) {
  return useMutation({
    mutationFn: ({ studentUuid, message }) =>
      api.students.flag(studentUuid, { message }),
    onSuccess: (updatedStudent) => {
      invalidateQueries.student(updatedStudent.uuid);
      invalidateQueries.students();
    },
    ...options,
  });
}

export default useStudents;